/**
 * 公開フォーム（未認証）の専用 DTO と入力検証（02 5.3.17・5.4・要件書 4.4 ⑥・9.1 ⑤）。
 * - PublicForm は社外に返してよい値だけ（form_name・通知先・閲覧数・回答数・作成者などを含めない）
 * - 回答の検証（buildAnswerSchema）は定義から組み立てる。画面（公開フォーム・widget）と public-form 関数で同じ関数を使う（03 8.4.2）
 * - 検証の文言は仕様書 10 章の MSG-V-001〜004・087・095・096・FE-MSG-V-002。差し込みの値は answerMessageParams で渡す
 */
import { z } from 'zod';
import type { ErrorDetailItem } from '../errors/error-codes';
import { isValidJstDate } from '../date/jst';
import { MAX_FILE_NAME_LENGTH, MAX_PUBLIC_UPLOAD_FILES } from '../files/file-rules';
import { t } from '../messages';
import type { AnyMessageId } from '../messages';
import { isMessageId } from '../messages';
import { zRequiredText, zShortText, zSystemCode, zUrl } from './common.schema';
import { FORM_FIELD_TYPE_VALUES } from './form-field-types';
import type { FormDefinition } from './form.schema';

export const PublicFormFieldSchema = z.object({
  field_key: z.string(),
  label: z.string(),
  field_type: z.enum(FORM_FIELD_TYPE_VALUES),
  is_required: z.boolean(),
  placeholder: z.string().nullable().optional(),
  help_text: z.string().nullable().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  validation: z
    .object({
      min_length: z.number().int().optional(),
      max_length: z.number().int().optional(),
      min_value: z.number().int().optional(),
      max_value: z.number().int().optional(),
    })
    .optional(),
  max_files: z.number().int().optional(),
});

/** API-PUBF-01 の応答（専用 DTO） */
export const PublicFormSchema = z.object({
  form_code: z.string(),
  form_title: z.string(),
  description: z.string().nullable(),
  body: z.string().nullable(),
  version_number: z.number().int(),
  fields: z.array(PublicFormFieldSchema),
  required_field_keys: z.array(z.string()),
  button_text: z.string(),
  button_color: z.string(),
  completion_message: z.string(),
  redirect_url: z.string().nullable(),
  // 273：旧PCRMから落ちていた同意必須を戻す（true なら送信に is_consented: true が要る）
  is_consent_required: z.boolean(),
});

/** API-PUBF-02 添付用の署名付き POST の発行（大きさ・形式の上限の判定は service：FILE_003・FILE_004） */
export const PublicUploadRequestSchema = z
  .object({
    form_submission_code: zSystemCode.optional(),
    files: z
      .array(
        z
          .object({
            field_key: z.string({ message: 'MSG-V-020' }).min(1, 'MSG-V-020').max(50, 'MSG-V-020'),
            file_name: zRequiredText(MAX_FILE_NAME_LENGTH, 'MSG-V-032'),
            file_size_bytes: z.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(1, 'MSG-V-020'),
            mime_type: z.string({ message: 'MSG-V-031' }).min(1, 'MSG-V-031').max(200, 'MSG-V-031'),
          })
          .strict(),
      )
      .min(1, 'MSG-V-032')
      .max(MAX_PUBLIC_UPLOAD_FILES, 'MSG-V-020'),
  })
  .strict();

export const PublicUploadResultSchema = z.object({
  form_submission_code: z.string(),
  uploads: z.array(
    z.object({
      attachment_code: z.string(),
      field_key: z.string(),
      upload_url: z.string(),
      upload_fields: z.record(z.string()),
      expires_at: z.string(),
    }),
  ),
});

/** API-PUBF-03 回答の送信（answers の中身は buildAnswerSchema で検証する） */
export const PublicSubmitRequestSchema = z
  .object({
    answers: z.record(z.unknown(), { message: 'MSG-V-020' }),
    form_submission_code: zSystemCode.optional(),
    embed_parent_url: zUrl.optional(),
    referrer_url: zShortText(2000).optional(),
    utm_source: zShortText(2000).optional(),
    utm_medium: zShortText(2000).optional(),
    utm_campaign: zShortText(2000).optional(),
    // 273：個人情報の取り扱いへの同意（フォームの is_consent_required が true のときは true が必須）
    is_consented: z.boolean({ message: 'MSG-V-020' }).optional(),
  })
  .strict();

/** 273：同意の欄の名前（VALIDATION_001 の details の field） */
export const CONSENT_FIELD_KEY = 'is_consented';

/**
 * 273：旧PCRMから落ちていた同意必須を戻す（旧 validateSubmission と同じ：必須なのに is_consented が true でなければ誤り）。
 * 画面（公開フォーム・widget）と public-form 関数で同じ関数を使う
 */
export function consentErrorDetail(is_consent_required: boolean, consent: unknown): ErrorDetailItem | null {
  if (!is_consent_required || consent === true) {
    return null;
  }
  return { field: CONSENT_FIELD_KEY, code: 'FE-MSG-V-016', message: t('FE-MSG-V-016') };
}

export const PublicSubmitResultSchema = z.object({
  form_submission_code: z.string(),
  submitted_at: z.string(),
  completion_message: z.string(),
  redirect_url: z.string().nullable(),
});

export type PublicFormField = z.infer<typeof PublicFormFieldSchema>;
export type PublicForm = z.infer<typeof PublicFormSchema>;
export type PublicUploadRequest = z.infer<typeof PublicUploadRequestSchema>;
export type PublicUploadResult = z.infer<typeof PublicUploadResultSchema>;
export type PublicSubmitRequest = z.infer<typeof PublicSubmitRequestSchema>;
export type PublicSubmitResult = z.infer<typeof PublicSubmitResultSchema>;

/** 定義 → 公開用の設問（社内の値を含めない：5.4 ⑥） */
export function toPublicFields(definition: FormDefinition): PublicFormField[] {
  return definition.fields.map((field) => ({
    field_key: field.field_key,
    label: field.label,
    field_type: field.field_type,
    is_required: field.is_required,
    placeholder: field.placeholder ?? null,
    help_text: field.help_text ?? null,
    ...(field.options ? { options: field.options } : {}),
    ...(field.validation ? { validation: field.validation } : {}),
    ...(field.max_files !== undefined ? { max_files: field.max_files } : {}),
  }));
}

/** 文字の回答の既定の上限（定義に max_length が無いとき） */
export const TEXT_ANSWER_MAX_LENGTH = 10000;
const TEL_PATTERN = /^[0-9\-+()]{10,20}$/;
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 空の入力（空文字・空白・空配列）を「回答なし」にする */
function blankToUndefined(value: unknown): unknown {
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }
  if (Array.isArray(value) && value.length === 0) {
    return undefined;
  }
  return value === null ? undefined : value;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function valueSchemaOf(field: PublicFormField): z.ZodTypeAny {
  const limits = field.validation ?? {};
  const option_values = new Set((field.options ?? []).map((option) => option.value));
  switch (field.field_type) {
    case 'text':
    case 'textarea': {
      let schema = z.string({ message: 'MSG-V-020' }).trim();
      if (limits.min_length !== undefined) {
        schema = schema.min(limits.min_length, 'MSG-V-003');
      }
      return schema.max(limits.max_length ?? TEXT_ANSWER_MAX_LENGTH, 'MSG-V-001');
    }
    case 'email':
      return z.string({ message: 'FE-MSG-V-002' }).trim().max(254, 'FE-MSG-V-002').regex(EMAIL_PATTERN, 'FE-MSG-V-002');
    case 'tel':
      return z.string({ message: 'MSG-V-096' }).trim().regex(TEL_PATTERN, 'MSG-V-096');
    case 'url':
      return z
        .string({ message: 'MSG-V-095' })
        .trim()
        .max(2000, 'MSG-V-095')
        .refine((value) => isHttpUrl(value), 'MSG-V-095');
    case 'number':
      return z.preprocess(
        (value) => (typeof value === 'string' && NUMBER_PATTERN.test(value.trim()) ? Number(value.trim()) : value),
        z
          .number({ message: 'MSG-V-020' })
          .finite('MSG-V-020')
          .superRefine((value, context) => {
            if (limits.min_value !== undefined && value < limits.min_value) {
              context.addIssue({ code: z.ZodIssueCode.custom, message: 'MSG-V-004' });
            }
            if (limits.max_value !== undefined && value > limits.max_value) {
              context.addIssue({ code: z.ZodIssueCode.custom, message: 'MSG-V-002' });
            }
          }),
      );
    case 'select':
    case 'radio':
      return z.string({ message: 'MSG-V-020' }).refine((value) => option_values.has(value), 'MSG-V-020');
    case 'checkbox':
      return z
        .array(
          z.string({ message: 'MSG-V-020' }).refine((value) => option_values.has(value), 'MSG-V-020'),
          {
            message: 'MSG-V-020',
          },
        )
        .refine((values) => new Set(values).size === values.length, 'MSG-V-020');
    case 'date':
      return z
        .string({ message: 'MSG-V-020' })
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'MSG-V-020')
        .refine((value) => isValidJstDate(value), 'MSG-V-020');
    case 'file':
      return z
        .array(zSystemCode, { message: 'MSG-V-020' })
        .max(field.max_files ?? 1, 'MSG-V-020')
        .refine((values) => new Set(values).size === values.length, 'MSG-V-020');
  }
}

/**
 * 回答の Zod を定義から組み立てる（03 8.4.2・02 API-PUBF-03）。
 * 必須は is_required と required_field_keys（api 方式向け）の両方。定義に無いキーは受け付けない。
 */
export function buildAnswerSchema(fields: readonly PublicFormField[], required_field_keys: readonly string[] = []) {
  const required_keys = new Set(required_field_keys);
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    const value_schema = valueSchemaOf(field);
    const is_required = field.is_required || required_keys.has(field.field_key);
    shape[field.field_key] = z.preprocess(
      blankToUndefined,
      is_required
        ? z
            .unknown()
            .refine((value) => value !== undefined, 'MSG-V-087')
            .pipe(value_schema)
        : value_schema.optional(),
    );
  }
  return z.object(shape).strict();
}

/** 文言の差し込みの値（MSG-V-001「${field.validation.max_length}文字以内…」など） */
export function answerMessageParams(field: PublicFormField | undefined): Record<string, number> {
  const limits = field?.validation ?? {};
  const params: Record<string, number> = Object.fromEntries(
    Object.entries(limits).filter(([, value]) => value !== undefined),
  );
  if ((field?.field_type === 'text' || field?.field_type === 'textarea') && params.max_length === undefined) {
    params.max_length = TEXT_ANSWER_MAX_LENGTH;
  }
  return params;
}

/** 回答の検証の誤り → 400 VALIDATION_001 の details（field は field_key） */
export function answerErrorDetails(fields: readonly PublicFormField[], error: z.ZodError): ErrorDetailItem[] {
  const by_key = new Map(fields.map((field) => [field.field_key, field]));
  const details: ErrorDetailItem[] = [];
  for (const issue of error.issues) {
    if (issue.code === 'unrecognized_keys') {
      for (const key of issue.keys) {
        details.push({ field: key, code: 'MSG-V-020', message: t('MSG-V-020') });
      }
      continue;
    }
    const field_key = String(issue.path[0] ?? '');
    const code: AnyMessageId = isMessageId(issue.message) ? issue.message : 'MSG-V-020';
    details.push({ field: field_key, code, message: t(code, answerMessageParams(by_key.get(field_key))) });
  }
  return details;
}

/** 回答の中の添付（file の欄の attachment_code）を集める */
export function attachmentCodesOfAnswers(
  fields: readonly PublicFormField[],
  answers: Readonly<Record<string, unknown>>,
): Array<{ field_key: string; attachment_code: string }> {
  const codes: Array<{ field_key: string; attachment_code: string }> = [];
  for (const field of fields) {
    const value = answers[field.field_key];
    if (field.field_type === 'file' && Array.isArray(value)) {
      for (const code of value) {
        if (typeof code === 'string') {
          codes.push({ field_key: field.field_key, attachment_code: code });
        }
      }
    }
  }
  return codes;
}
